import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import EntryCodeModal from '../../components/shared/EntryCodeModal';
import { PatientQuestionnaire } from './PatientQuestionnaire';
import ErrorPage from '../ErrorPage';
import './TokenQuestionnaire.css';

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

interface TokenQuestionnaireProps {}

interface TokenValidationData {
  questionnaire_type: string;
  expires_at: string;
  entry_attempts: number;
  max_attempts: number;
  requires_entry_code: boolean;
  language?: string;  // Add language support
}

interface QuestionnaireAccessData {
  access_granted: boolean;
  patient_id: string;
  questionnaire_type: string;
  questionnaire_data: any;
  language?: string;  // Add language support
}

const TokenQuestionnaire: React.FC<TokenQuestionnaireProps> = () => {
  const { displayToken } = useParams<{ displayToken: string }>();  // Changed from shortId
  
  const [isLoading, setIsLoading] = useState(true);
  const [showEntryModal, setShowEntryModal] = useState(false);
  const [tokenData, setTokenData] = useState<TokenValidationData | null>(null);
  const [questionnaireAccess, setQuestionnaireAccess] = useState<QuestionnaireAccessData | null>(null);
  const [error, setError] = useState<{ type: string; message: string } | null>(null);
  const [hasValidSession, setHasValidSession] = useState(false);

  useEffect(() => {
    if (!displayToken) {
      setError({ type: 'invalid_link', message: 'Μη έγκυρος σύνδεσμος' });
      setIsLoading(false);
      return;
    }

    // Validate display_token format (8 characters, alphanumeric)
    if (!/^[A-Z0-9]{8}$/.test(displayToken)) {
      setError({ type: 'invalid_link', message: 'Μη έγκυρη μορφή συνδέσμου' });
      setIsLoading(false);
      return;
    }

    accessQuestionnaireLink();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayToken]);

  const accessQuestionnaireLink = async () => {
    try {
      setIsLoading(true);
      
      // NEW ARCHITECTURE: Call the new link access endpoint
      const response = await fetch(`${API_BASE_URL}/api/questionnaire/${displayToken}`, {
        credentials: 'include' // Include cookies for session authentication
      });
      const result = await response.json();

      if (response.ok) {
        // Check access type per new architecture
        if (result.data.access_type === 'form') {
          // Valid session exists - direct access to form
          setHasValidSession(true); // User has returning session
          setQuestionnaireAccess({
            access_granted: true,
            patient_id: result.data.patient_id,
            questionnaire_type: result.data.questionnaire_type,
            questionnaire_data: result.data.questionnaire_data,
            language: result.data.language || 'el'  // Capture language from response
          });
        } else if (result.data.access_type === 'otp_required') {
          // OTP required - show entry modal
          setTokenData({
            questionnaire_type: result.data.questionnaire_type,
            expires_at: result.data.token_info?.link_expires_at || '',
            entry_attempts: result.data.token_info?.failed_attempts || 0,
            max_attempts: result.data.token_info?.max_attempts || 5,
            requires_entry_code: true,
            language: result.data.language || 'el'  // Capture language from response
          });
          setShowEntryModal(true);
        }
      } else {
        handleLinkAccessError(response.status, result.detail || result.message);
      }
    } catch (error) {
      console.error('Link access error:', error);
      setError({ 
        type: 'general', 
        message: 'Για λόγους ασφάλειας το link κλειδώθηκε. Επικοινωνήστε με τον γιατρό.' 
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkAccessError = (status: number, message: string) => {
    // Handle errors according to new architecture status codes
    if (status === 410) {
      setError({ type: 'expired_link', message });
    } else if (status === 423) {
      setError({ type: 'locked_link', message });
    } else if (status === 404) {
      setError({ type: 'invalid_link', message });
    } else {
      setError({ type: 'unauthorized', message });
    }
  };

  const handleEntryCodeSuccess = (accessData: QuestionnaireAccessData) => {
    console.log('Entry code verified, access granted:', accessData);
    setHasValidSession(false); // User went through OTP, so this is first-time access
    setQuestionnaireAccess(accessData);
    setShowEntryModal(false);
  };

  const handleEntryCodeError = (errorMessage: string) => {
    console.error('Entry code error:', errorMessage);
    
    if (errorMessage.includes('expired')) {
      setError({ type: 'expired_link', message: errorMessage });
      setShowEntryModal(false);
    } else if (errorMessage.includes('κλειδώθηκε')) {
      // If locked, show error but don't redirect - user stays on page
      setError({ type: 'locked_link', message: errorMessage });
      setShowEntryModal(false);
    }
    // For other errors, keep the modal open so user can try again
  };



  // Show loading screen
  if (isLoading) {
    return (
      <div className="token-questionnaire-loading">
        <div className="loading-container">
          <div className="loading-spinner-large"></div>
          <h2>Επαλήθευση συνδέσμου...</h2>
          <p>Παρακαλώ περιμένετε</p>
        </div>
      </div>
    );
  }

  // Show error page
  if (error) {
    return (
      <ErrorPage 
        errorType={error.type as any}
        message={error.message}
      />
    );
  }

  // Show questionnaire if access is granted
  if (questionnaireAccess) {
    return (
      <PatientQuestionnaire 
        patientId={questionnaireAccess.patient_id}
        questionnaireType={questionnaireAccess.questionnaire_type}
        questionnaireData={questionnaireAccess.questionnaire_data}
        isTokenBased={true}
        language={questionnaireAccess.language as 'en' | 'el'}
        displayToken={displayToken} // Pass display token for submission
        hasValidSession={hasValidSession} // Pass session info for GDPR logic
      />
    );
  }

  // Show entry code modal
  return (
    <>
      {showEntryModal && tokenData && (
        <EntryCodeModal
          isOpen={showEntryModal}
          displayToken={displayToken!}
          onSuccess={handleEntryCodeSuccess}
          onError={handleEntryCodeError}
          type="questionnaire"
          initialLanguage={tokenData.language as 'en' | 'el'}
        />
      )}
      
      {/* Background content while modal is shown */}
      <div className="token-questionnaire-background">
        <div className="background-content">
          <div className="security-bg-icon">🔒</div>
          <h1>Ασφαλής Πρόσβαση</h1>
          <p>Παρακαλώ εισάγετε τον κωδικό ασφαλείας για να συνεχίσετε</p>
        </div>
      </div>
    </>
  );
};

export default TokenQuestionnaire;